import React, { useState, useEffect, useRef, useMemo } from "react";
import { toast, Bounce, ToastContainer } from "react-toastify";
import html2canvas from "html2canvas";
import InputBox from "../reusable/InputBox";
import { submitFormData } from "../../services/formService";
import "react-toastify/dist/ReactToastify.css";
import { useTranslation } from "react-i18next";

const TRANSACTION_TYPES = {
  MONEY_GIVEN: "Money Given",
  MONEY_RECEIVED: "Money Received",
};

function DefaultEntry({ userId, userSelectedOption, role, Name }) {
  const { t } = useTranslation();
  
  const [selectedOption, setSelectedOption] = useState("Feed"); 
  const [date, setDate] = useState("");
  const [quantity, setQuantity] = useState("");
  const [price, setPrice] = useState("");
  const [moneyAmount, setMoneyAmount] = useState("");
  const [showUserAlert, setShowUserAlert] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const [showReceiptPopup, setShowReceiptPopup] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const receiptRef = useRef(null);

  const isMoneyTransaction = useMemo(
    () => Object.values(TRANSACTION_TYPES).includes(selectedOption),
    [selectedOption]
  );

  const totalAmount = useMemo(() => {
    if (isMoneyTransaction) return Number(moneyAmount || 0).toFixed(2);
    return (Number(quantity || 0) * Number(price || 0)).toFixed(2);
  }, [quantity, price, moneyAmount, isMoneyTransaction]);

  useEffect(() => {
    if (userSelectedOption) setSelectedOption(userSelectedOption);
    setDate(new Date().toISOString().split("T")[0]);
  }, [userSelectedOption]);

  const getTranslatedOption = (optionKey) => {
    return t(`defaultEntry.options.${optionKey}`, optionKey); 
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userId && role === "admin") {
      setShowUserAlert(true);
      return;
    }

    const formData = {
      userId,
      date,
      selectedOption,
      quantity,
      price,
      moneyAmount,
    };

    const endpoint =
      selectedOption === TRANSACTION_TYPES.MONEY_GIVEN
        ? "/addMoney"
        : selectedOption === TRANSACTION_TYPES.MONEY_RECEIVED
        ? "/receiveMoney"
        : "/items";

    try {
      await toast.promise(submitFormData(endpoint, formData), {
        pending: t('defaultEntry.alerts.submitting'),
        success: t('defaultEntry.alerts.success'),
        error: t('defaultEntry.alerts.failed'),
      });

      if (role === "admin") {
        setReceiptData({
          ...formData,
          total: totalAmount,
          time: new Date().toLocaleTimeString(),
          userName: Name,
        });
        setShowReceiptPopup(true);
      }

      setQuantity("");
      setPrice("");
      setMoneyAmount("");
    } catch (err) {
      console.error(err);
    }
  };

  const generateReceiptImage = async () => {
    const canvas = await html2canvas(receiptRef.current, {
      scale: 2,
      backgroundColor: "#ffffff",
    });
    return canvas;
  };

  const printReceipt = async () => {
    const canvas = await generateReceiptImage();
    const win = window.open("", "_blank");
    win.document.write(`<img src="${canvas.toDataURL()}" style="width:100%"/>`);
    setTimeout(() => {
        win.print();
        win.close();
    }, 500);
  };

  const shareReceipt = async () => {
    setIsProcessing(true);
    try {
      const canvas = await generateReceiptImage();
      const blob = await new Promise((r) => canvas.toBlob(r));
      const file = new File([blob], "receipt.png", { type: "image/png" });

      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: t('defaultEntry.receipt.title') });
      } else {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "receipt.png";
        link.click();
      }
    } catch {
      toast.error(t('defaultEntry.alerts.share_fail'));
    }
    setIsProcessing(false);
  };

  return (
    <div className="w-full  font-sans text-primary">
      {/* USER ALERT */}
      {showUserAlert && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white border border-line rounded-2xl p-6 w-80 text-center shadow-xl">
            <h3 className="text-lg font-bold text-debit mb-2">
              {t('defaultEntry.alerts.cust_title')}
            </h3>
            <p className="text-[13px] text-[#6B7280] mb-4">
              {t('defaultEntry.alerts.cust_msg')}
            </p>
            <button
              onClick={() => setShowUserAlert(false)}
              className="bg-paper hover:bg-line text-primary px-6 py-2 rounded-xl font-bold text-[13px] transition-colors"
            >
              {t('defaultEntry.alerts.close')}
            </button>
          </div>
        </div>
      )}

      {/* RECEIPT ACTIONS MODAL */}
      {showReceiptPopup && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white border border-line rounded-2xl p-6 w-full max-w-sm space-y-3 shadow-2xl">
            <h2 className="text-lg font-bold text-center text-primary">
              {t('defaultEntry.receipt.options_title')}
            </h2>
            <button
              onClick={printReceipt}
              className="w-full bg-primary hover:bg-primary/90 text-white py-3 rounded-xl font-bold text-[14px] transition-all active:scale-95 shadow-sm"
            >
              {t('defaultEntry.receipt.print')}
            </button>
            <button
              onClick={shareReceipt}
              disabled={isProcessing}
              className="w-full bg-credit hover:bg-credit/90 text-white py-3 rounded-xl font-bold text-[14px] disabled:opacity-50 transition-all active:scale-95 shadow-sm"
            >
              {isProcessing ? t('defaultEntry.receipt.processing') : t('defaultEntry.receipt.share')}
            </button>
            <button
              onClick={() => setShowReceiptPopup(false)}
              className="w-full py-2 text-[#6B7280] font-medium text-[13px] hover:text-primary transition-colors"
            >
              {t('defaultEntry.alerts.close')}
            </button>
          </div>
        </div>
      )}

      {/* HIDDEN RECEIPT */}
      <div className="absolute left-[-9999px] top-0">
        <div
          ref={receiptRef}
          className="w-[350px] p-6 text-sm bg-white text-black"
          style={{ fontFamily: "'Courier New', monospace" }}
        >
          <h2 className="text-center font-bold border-b border-dashed border-black pb-3 mb-3 text-lg">
            {t('defaultEntry.receipt.title')}
          </h2>
          <div className="space-y-1 mb-2">
             <div className="flex justify-between"><span>{t('defaultEntry.receipt.date')}:</span> <span>{receiptData?.date}</span></div>
             <div className="flex justify-between"><span>{t('defaultEntry.receipt.customer')}:</span> <span>{Name}</span></div>
             <div className="flex justify-between">
                <span>{t('defaultEntry.receipt.type')}:</span> 
                <span>{getTranslatedOption(receiptData?.selectedOption)}</span>
             </div>
          </div>
          
          <hr className="border-dashed border-black my-3" />
          
          <div className="flex justify-between font-bold text-xl my-4">
            <span>{t('defaultEntry.receipt.total')}</span>
            <span>₹{receiptData?.total}</span>
          </div>
          
          <hr className="border-dashed border-black my-3" />
          <p className="text-center mt-4 text-xs">{t('defaultEntry.receipt.thank_you')}</p>
        </div>
      </div>

      {/* FORM CARD */}
      <div className="mx-auto max-w-lg rounded-[26px] bg-white p-5 shadow-[0_12px_34px_rgba(20,35,58,0.08)] ring-1 ring-[#E9EDF3] md:p-6">
        <form onSubmit={handleSubmit} className="space-y-5">
          
          {/* Date */}
          <InputBox 
             type="date" 
             label={t('defaultEntry.form.date')} 
             value={date} 
             onValueChange={(e) => setDate(e.target.value)} 
          />

          {/* Selected Option Badge */}
          <div>
            <label className="mb-2 ml-0.5 block text-[12px] font-bold text-ink">
                {t('defaultEntry.form.type')}
            </label>
            <div className="rounded-2xl bg-primary/[0.06] px-4 py-3.5 text-[14px] font-bold text-primary ring-1 ring-primary/10">
              {getTranslatedOption(selectedOption)}
            </div>
          </div>

          {/* Conditional Inputs Block */}
          {isMoneyTransaction ? (
            <InputBox
              type="number"
              label={t('defaultEntry.form.amount')}
              value={moneyAmount}
              onValueChange={(e) => setMoneyAmount(e.target.value)}
              required
              placeholder="0.00"
            />
          ) : (
            <div className="grid grid-cols-2 gap-3 rounded-[20px] bg-paper p-3.5">
              <InputBox 
                type="number" 
                label={t('defaultEntry.form.quantity')} 
                value={quantity} 
                onValueChange={(e) => setQuantity(e.target.value)} 
                required 
                placeholder="0"
              />
              <InputBox 
                type="number" 
                label={t('defaultEntry.form.price')} 
                value={price} 
                onValueChange={(e) => setPrice(e.target.value)} 
                required 
                placeholder="0.00"
              />
            </div>
          )}

          {/* Receipt Style Total Amount Summary */}
          {totalAmount > 0 && (
            <div className="flex flex-col rounded-[20px] bg-credit/[0.08] p-4 ring-1 ring-credit/10">
              <span className="mb-1 text-[11px] font-bold text-credit">
                {t('defaultEntry.form.total')}
              </span>
              <div className="text-[28px] font-mono font-bold text-credit leading-none">
                ₹{totalAmount}
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            className="app-primary-button w-full text-[15px]"
          >
            {t('defaultEntry.form.submit_btn')}
          </button>
        </form>
      </div>

      <ToastContainer position="top-center" transition={Bounce} />
    </div>
  );
}

export default DefaultEntry;
